"use client";
import { Button } from "./button";
import React from "react";
import { useRouter } from "next/navigation";

import { useState, useRef, useEffect } from "react";
import {
  Search,
  ChevronRight,
  X,
  Menu,
  Camera,
  Shield,
  Radio,
  Network,
  Bot,
  Flame,
  Siren,
  Wifi,
  Video,
  ShoppingBag,
  HousePlug,
  BrickWallFire,
  Monitor,
  Tv2,
  Antenna,
  Cctv,
  LayoutGrid,
} from "lucide-react";
import { Categories } from "@/lib/models/header";
import { useLatestRequest } from "@/lib/hooks/useLatestRequest";
import { useApi } from "@/lib/hooks/useApi";
import { category, CategorySearchItem } from "@/lib/models/category";
import { searchMinChars } from "@/lib/utils/constans";
import MegaMenuResponsive from "./MegaMenuResponsive";

interface MegaMenuProps {
  categories: Categories[];
  buttonStyles: string;
}

// Iconos por categoria (puedes mapear segun el nombre)
const categoryIcons: Record<string, React.ReactNode> = {
  "control-de-acceso": <Shield className="w-5 h-5" />,
  videovigilancia: <Camera className="w-5 h-5" />,
  redes: <Network className="w-5 h-5" />,
  "robots-e-industrial": <Bot className="w-5 h-5" />,
  "deteccion-de-fuego": <Flame className="w-5 h-5" />,
  "punto-de-venta": <ShoppingBag className="w-5 h-5" />,
  radiocomunicacion: <Radio className="w-5 h-5" />,
  conectividad: <Wifi className="w-5 h-5" />,
  video: <Video className="w-5 h-5" />,
  "deteccion-de-incendio": <BrickWallFire className="w-5 h-5" />,
  intrusion: <Siren className="w-5 h-5" />,
  energia: <HousePlug className="w-5 h-5" />,
  mp3: <Monitor className="w-5 h-5" />,
  "punto-de-venta-v-2": <ShoppingBag className="w-5 h-5" />,
  monitores: <Monitor className="w-5 h-5" />,
  transmisiones: <Antenna />,
};

function getIcon(slug: string) {
  if (categoryIcons[slug]) return categoryIcons[slug];
  return <LayoutGrid />;
}

export function MegaMenu({ categories, buttonStyles }: MegaMenuProps) {
  const [selectedLevel1, setSelectedLevel1] = useState<Categories | null>(
    null,
  );
  const [hoveredLevel2, setHoveredLevel2] = useState<Categories | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [listSearch, setlistSearch] = useState<CategorySearchItem[]>([]);
  const [isOpen, setisOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const { getNextRequestId, isCurrent, invalidate } = useLatestRequest();
  const { apiRequest } = useApi("");

  // Cerrar al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setisOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Reset al cerrar
  useEffect(() => {
    if (!isOpen) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      invalidate();
      setSelectedLevel1(null);
      setHoveredLevel2(null);
      setSearchTerm("");
      setlistSearch([]);
    }
  }, [isOpen, invalidate]);

  // Limpiar timer e invalidar al desmontar
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      invalidate();
    };
  }, [invalidate]);

  const router = useRouter();

  function handleRedirect(path: string) {
    setisOpen(false);
    router.push(`/productos?categoryPath=${path}`);
  }

  function handleFilterMenu(term: string) {
    setSearchTerm(term);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (term.trim().length < searchMinChars) {
      invalidate();
      setlistSearch([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const requestId = getNextRequestId();
      try {
        const { data } = await apiRequest<{ data: { items: CategorySearchItem[] } }>("", {
          method: "post",
          loadingMessage: "Buscando categorias...",
          url: `${process.env.NEXT_PUBLIC_API_URL}/search/categories`,
          data: { query: term.trim(), page: 0, size: 60 },
          headers: { "Content-Type": "application/json" },
        });

        if (isCurrent(requestId)) {
          setlistSearch(data?.items || []);
        }
      } catch (error) {
        if (isCurrent(requestId)) {
          setlistSearch([]);
        }
      }
    }, 300);
  }

  return (
    <>
      {isOpen ? (
        <div className="fixed inset-0 z-50 bg-black/80">
          <div
            ref={menuRef}
            className={`
              fixed inset-0 z-50
              bg-white shadow-2xl
              border border-gray-200
              flex flex-col
              w-full h-full rounded-none rounded-t-none
              md:relative md:top-0 md:left-0 md:right-0
              md:mx-auto md:mt-4 md:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl md:rounded-lg md:h-[90vh]
              md:w-full
              md:border md:border-gray-200
              overflow-hidden
            `}
          >
            <div className=" hidden md:block">
              {/* Header con busqueda */}
              <div className="flex items-center gap-4 p-4 border-gray-100 border-b bg-white sticky top-0 z-10 transition-shadow">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar en el menú"
                    value={searchTerm}
                    onChange={(e) => handleFilterMenu(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setisOpen(false)}
                    className="p-2 rounded-xl transition-colors text-gray-400 hover:bg-gray-100"
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>
              </div>

              {/* Contenido del menu */}
              <div
                className="
                 flex flex-1 min-h-0
                flex-col md:flex-row
                overflow-hidden
                max-h-[calc(100vh-56px)]
              "
              >
                {/* Si hay búsqueda, mostrar resultados agrupados en columnas */}
                {searchTerm !== "" && listSearch.length > 0 ? (
                  <div className="w-full h-full p-4 overflow-y-auto">
                    {listSearch.map((item) => (
                      <div key={item.id} className="mt-1 mb-1">
                        <Button
                          variant="ghost"
                          className="text-left text-sm  !text-black hover:!text-primary"
                          onClick={() => handleRedirect(item.path)}
                        >
                          {item?.mapNameId?.map((crumb, i) => (
                            <span key={i}>
                              {crumb.NameCategory}
                              {i < (item.mapNameId?.length ?? 0) - 1 && " > "}
                            </span>
                          ))}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Nivel 1 - Categorias principales (click) */}
                    <div
                      className="
                      w-full md:basis-80 md:w-80
                      border-b md:border-b-0 md:border-r border-gray-100
                      overflow-y-auto bg-gray-50/30 custom-scrollbar
                      flex-shrink-0
                      min-h-0
                    "
                    >
                      <div className="py-4 px-2 space-y-1">
                        {categories &&
                          categories?.map((category) => (
                            <button
                              key={category.id}
                              onClick={() => handleRedirect(category.path)}
                              onMouseEnter={() => {
                                setSelectedLevel1(category);
                                setHoveredLevel2(null);
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all cursor-pointer group ${
                                selectedLevel1?.id === category.id
                                  ? "text-primary bg-blue-50/50 shadow-sm"
                                  : "hover:bg-gray-100/50 text-gray-500 hover:text-gray-700"
                              }`}
                            >
                              <span
                                className={`transition-colors ${
                                  selectedLevel1?.id === category.id
                                    ? "text-primary"
                                    : "text-gray-400 group-hover:text-gray-600"
                                }`}
                              >
                                {getIcon(category.slug)}
                              </span>
                              <span
                                className={`text-sm font-medium ${selectedLevel1?.id === category.id ? "font-" : ""}`}
                              >
                                {category.name}
                              </span>
                            </button>
                          ))}
                      </div>
                    </div>

                    {/* Nivel 2 - Subcategorias (hover) */}
                    {selectedLevel1 && selectedLevel1?.children?.length > 0 && (
                      <div
                        className="
                        w-full md:basis-80 md:w-80
                        border-b md:border-b-0 md:border-r border-gray-200
                        overflow-y-auto custom-scrollbar
                        flex-shrink-0
                        min-h-0
                        cursor-pointer
                      "
                      >
                        <div className="pl-3 pt-5 pb-4 sticky top-0 bg-white z-10">
                          <div className="flex items-center gap-3">
                            <div className="w-1 h-5 bg-primary rounded-full transition-all" />
                            <h3 className="text-lg text-primary font-bold tracking-tight">
                              {selectedLevel1.name}
                            </h3>
                          </div>
                        </div>

                        <div className="py-1 space-y-0.5">
                          {selectedLevel1.children.map((subcat: Categories) => (
                            <button
                              key={subcat.id}
                              onMouseEnter={() => setHoveredLevel2(subcat)}
                              onClick={() => handleRedirect(subcat.path)}
                              className={`w-full flex items-center justify-between px-5 py-2.5 text-left transition-all cursor-pointer group border-l-4 ${
                                hoveredLevel2?.id === subcat.id
                                  ? "text-primary bg-blue-50/50 border-primary"
                                  : "hover:bg-gray-50 text-gray-600 border-transparent"
                              }`}
                            >
                              <span
                                className={`text-[15px] ${
                                  hoveredLevel2?.id === subcat.id
                                    ? "font-semibold"
                                    : "font-medium"
                                }`}
                              >
                                {subcat.name}
                              </span>
                              {subcat?.children?.length > 0 && (
                                <ChevronRight
                                  className={`w-4 h-4 transition-transform ${
                                    hoveredLevel2?.id === subcat.id
                                      ? "text-primary translate-x-1"
                                      : "text-gray-300 group-hover:text-gray-400"
                                  }`}
                                />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Nivel 3 - Items finales */}
                    {hoveredLevel2 && hoveredLevel2?.children?.length > 0 && (
                      <div className="flex-1 min-w-0 overflow-y-auto bg-white custom-scrollbar">
                        <div className="pl-6 pt-5 pb-4 sticky top-0 bg-white z-10">
                          <div className="flex items-center gap-3">
                            <div className="w-1 h-5 bg-primary rounded-full transition-all" />
                            <h3 className="text-lg text-primary font-bold tracking-tight">
                              {hoveredLevel2.name}
                            </h3>
                          </div>
                        </div>
                        <div className="px-6 py-2">
                          <div className="grid grid-cols-1 gap-0.5">
                            {hoveredLevel2.children.map((item: Categories) => (
                              <button
                                key={item.id}
                                onClick={() => handleRedirect(item.path)}
                                className="cursor-pointer text-start px-4 py-2 text-[14px] text-gray-500 hover:text-primary hover:bg-blue-50/30 rounded-lg transition-all"
                              >
                                {item.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Estado vacio nivel 2 */}
                    {selectedLevel1 &&
                      selectedLevel1?.children?.length === 0 && (
                        <div className="flex-1 flex items-center justify-center text-gray-400">
                          <p>No hay subcategorias disponibles</p>
                        </div>
                      )}

                    {/* Estado inicial */}
                    {!selectedLevel1 && (
                      <div className="flex-1 flex items-center justify-center text-gray-400">
                        <p>
                          Selecciona una categoria para ver las subcategorias
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="block md:hidden flex flex-col h-full overflow-hidden">
              {/* Header de búsqueda siempre visible en móvil */}
              <div className="flex items-center gap-4 p-2 border-gray-200 border-b bg-white sticky top-0 z-10">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
                  <input
                    type="text"
                    placeholder="Buscar en el menu..."
                    value={searchTerm}
                    onChange={(e) => handleFilterMenu(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-primary rounded-lg text-black placeholder-gray-500 focus:outline-none focus:border-primary"
                  />
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setisOpen(false)}
                  className="p-1 rounded-lg transition-colors text-black hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div
                className="
                flex flex-1 min-h-0
                flex-col
                overflow-hidden
              "
              >
                {/* Resultado de búsqueda o menú responsivo */}
                {searchTerm !== "" && listSearch.length > 0 ? (
                  <div className="w-full h-full overflow-y-auto p-2">
                    {listSearch.map((item) => (
                      <div key={item.id}>
                        <Button
                          variant="ghost"
                          className="text-left text-[11px] !p-0 !pl-2 !text-black hover:!text-primary"
                          onClick={() => handleRedirect(item.path)}
                        >
                          {item?.mapNameId?.map((crumb, i) => (
                            <span key={i}>
                              {crumb.NameCategory}
                              {i < (item.mapNameId?.length ?? 0) - 1 && " > "}
                            </span>
                          ))}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <MegaMenuResponsive
                    categories={categories as unknown as category[]}
                    buttonStyles={buttonStyles}
                    isOpen={isOpen}
                    setisOpen={setisOpen}
                    getIcon={getIcon}
                    handleRedirect={handleRedirect}
                    onSearch={handleFilterMenu}
                    searchTerm={searchTerm}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p onClick={() => setisOpen(true)} className={buttonStyles}>
          <Menu className="w-5  h-5 cursor-pointer" />
          <span className="hidden md:block text-md">Productos</span>
        </p>
      )}
    </>
  );
}
